/**
 * Shared public display rules for schedule text and session/semester labels.
 *
 * Two rules, applied on every public surface:
 *  1. No real session/option in the authoritative data → never render an
 *     inferred session or semester label (no "Fall Semester" guesses).
 *  2. No finalized schedule → render "TBD" rather than inviting parents to
 *     browse sessions that do not exist.
 *
 * Pure display logic — reads only what is already loaded, writes nothing.
 */
import type { ClassItem } from "@/data/classes";

const PLACEHOLDER_SCHEDULE = /^(tbd|tba|n\/a|-|—)$/i;

/** True when the class has at least one real session / schedule option. */
export function hasRealSessions(classItem: Pick<ClassItem, "sessions">): boolean {
  return Array.isArray(classItem.sessions) && classItem.sessions.length > 0;
}

/** True when the class has a finalized, human-meaningful schedule string. */
export function hasFinalizedSchedule(
  classItem: Pick<ClassItem, "schedule" | "sessions">,
): boolean {
  const s = (classItem.schedule ?? "").trim();
  if (s && !PLACEHOLDER_SCHEDULE.test(s)) return true;
  return hasRealSessions(classItem);
}

/** Parent-friendly schedule text for public surfaces. */
export function formatPublicSchedule(
  classItem: Pick<ClassItem, "schedule" | "sessions">,
): string {
  const s = (classItem.schedule ?? "").trim();
  if (!s || PLACEHOLDER_SCHEDULE.test(s)) {
    // Only point parents at sessions when sessions actually exist.
    return hasRealSessions(classItem) ? "See available sessions" : "TBD";
  }
  const lower = s.toLowerCase();
  if (lower.includes("time") && lower.includes("var")) {
    const [days] = s.split(/[—–-]/);
    const cleanDays = days.trim();
    return cleanDays ? `${cleanDays} · Multiple times` : "Multiple session times";
  }
  return s;
}

/**
 * The session/semester suffix allowed on a public CTA. Null unless the class
 * actually has a session or option backing that label.
 */
export function publicPeriodSuffix(
  classItem: Pick<ClassItem, "sessions">,
  nextPeriodLabel: string | null,
): string | null {
  if (!nextPeriodLabel) return null;
  return hasRealSessions(classItem) ? nextPeriodLabel : null;
}
